import { IdentityProviderRequired, identityProviderRequired } from '@shared/networkError';

import { AuthService } from '@/services/authService';
import { MUT_IS_LOADING, MUT_LOGOUT, store } from '@/store';

import { requestParentToNavigate } from './iframeMessageRequester';

/**
 * IDENTITY_PROVIDER_REQUIRED: the org only admits sign-ins from an approved
 * identity provider (today: a Google Workspace domain) and this session is
 * not one. The token is valid — it is the wrong KIND of session — so this is
 * not a 401 refresh case, and retrying would reproduce the refusal.
 *
 * Embedded in the v3 shell: hand off to the shell's /sign-in-required page,
 * which signs out and re-authorizes with the step-up hint. Standalone: do
 * the same here — drop the refused session and re-authorize for the org
 * with the hint, which makes auth-svc show its login page with the Google
 * chooser instead of silently reusing the refresh cookie.
 */

const SIGN_IN_REQUIRED_PATH = '/sign-in-required';
let handling = false;

/** Reset the in-flight guard (tests). */
export function resetIdentityProviderRequiredGuard() {
  handling = false;
}

/** Workspace domains from the refusal, normalized (empty when none). */
export function workspaceDomains(refusal: IdentityProviderRequired): string[] {
  return refusal.allowedIdentityProviders
    .filter((p) => p.type === 'google_workspace' && typeof p.domain === 'string' && p.domain)
    .map((p) => (p.domain as string).trim().toLowerCase());
}

function embeddedAuthPage(): boolean {
  const hash = window.location.hash;
  const params = hash.includes('?') ? new URLSearchParams(hash.split('?')[1]) : new URLSearchParams();
  return params.get('embedded') === 'true';
}

/**
 * Handle a refusal if `body` is one. Returns true when it was (the caller
 * should reject/abort), false otherwise. Idempotent per page: once a hand-off
 * is in flight, further refusals are swallowed rather than re-triggering it.
 */
export function handleIdentityProviderRequired(status: number, body: unknown, isGraphQL = false): boolean {
  const refusal = identityProviderRequired(status, body, isGraphQL);
  if (!refusal) return false;
  if (handling) return true;
  handling = true;

  const domains = workspaceDomains(refusal);
  const params: Record<string, string> = {};
  if (refusal.orgId) params.orgId = refusal.orgId;
  if (domains.length) params.hd = domains.join(',');

  if (window.self !== window.top && !embeddedAuthPage()) {
    // Inside the v3 shell: navigation decisions belong to the shell.
    requestParentToNavigate(SIGN_IN_REQUIRED_PATH, params);
    return true;
  }

  // Standalone: drop the refused session's tokens before re-authorizing — the
  // next sign-in may be a different Google account.
  localStorage.removeItem('refreshToken');
  store.commit(MUT_LOGOUT);
  store.commit(MUT_IS_LOADING, false);
  new AuthService()
    .sendToAuth(undefined, {
      orgId: refusal.orgId,
      stepUp: { method: 'google_workspace', hd: domains.length === 1 ? domains[0] : undefined },
    })
    .catch((e) => {
      console.error('Failed to redirect to the auth service for a Google Workspace sign-in', e);
      handling = false;
    });
  return true;
}
